import {
  ArrowDownOnSquareIcon,
  BookmarkIcon,
  CubeIcon,
  DocumentIcon,
  PhotoIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Pdf as PdfIcon } from '@/components/chariot-ui';
import {
  DocumentSource,
  SOURCE_BADGE_CONFIG,
} from '@/components/documents/documentSources';
import {
  RowAction,
  RowActionsMenu,
} from '@/sections/settings/components/primitives/RowActionsMenu';
import { MyFile, ParsedFileTypes } from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';
import { getFileType } from '@/utils/file.util';

function FileTypeIcon({ fileType }: { fileType: ParsedFileTypes }) {
  const className = 'text-chariot-text-secondary size-5';
  switch (fileType) {
    case ParsedFileTypes.IMAGE:
      return <PhotoIcon className={className} />;
    case ParsedFileTypes.PDF:
      return <PdfIcon className={cn(className, 'stroke-2')} />;
    case ParsedFileTypes.BINARY:
      return <CubeIcon className={className} />;
    default:
      return <DocumentIcon className={className} />;
  }
}

interface DocumentListItemProps {
  file: MyFile;
  onView: (file: MyFile) => void;
  onDownload: (file: MyFile) => void;
  onDelete?: (file: MyFile) => void;
  onShare?: (file: MyFile) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (file: MyFile) => void;
  displayName?: string;
  source?: DocumentSource;
}

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  file,
  onView,
  onDownload,
  onDelete,
  onShare,
  isFavorite = false,
  onToggleFavorite,
  displayName,
  source,
}) => {
  const fileType = getFileType(file.name);
  const name = displayName || file.name;

  const actions: RowAction[] = [];
  if (onToggleFavorite) {
    actions.push({
      label: isFavorite ? 'Unfavorite' : 'Favorite',
      icon: (
        <BookmarkIcon
          className={cn(
            'size-4',
            isFavorite ? 'fill-current text-yellow-500' : ''
          )}
        />
      ),
      onClick: () => onToggleFavorite(file),
    });
  }
  actions.push({
    label: 'Download',
    icon: <ArrowDownOnSquareIcon className="size-4" />,
    onClick: () => onDownload(file),
  });
  if (onDelete) {
    actions.push({
      label: 'Delete',
      icon: <TrashIcon className="size-4" />,
      onClick: () => onDelete(file),
      variant: 'danger',
    });
  }

  const badgeConfig = source ? SOURCE_BADGE_CONFIG[source] : null;

  return (
    <div className="border-chariot-stroke hover:bg-chariot-background-secondary flex items-center gap-4 border-t px-4 py-3 transition-colors">
      {/* File icon + name */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="shrink-0">
          <FileTypeIcon fileType={fileType} />
        </div>
        <button
          className="text-chariot-text-primary truncate text-left text-sm font-medium hover:underline"
          onClick={() => onView(file)}
        >
          {name}
        </button>
      </div>

      {/* Date */}
      <div className="text-chariot-text-secondary w-32 shrink-0 text-sm">
        {formatDate(file.updated)}
      </div>

      {/* Source badge */}
      <div className="w-28 shrink-0">
        {badgeConfig && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
              badgeConfig.className
            )}
          >
            {badgeConfig.label}
          </span>
        )}
      </div>

      {/* Share link */}
      <div className="w-8 shrink-0">
        {onShare && (
          <button
            className="text-chariot-text-secondary hover:text-chariot-text-primary rounded p-1 transition-colors"
            onClick={e => {
              e.stopPropagation();
              onShare(file);
            }}
            aria-label="Copy share link"
            data-testid={`share-link-${file.name}`}
          >
            <ShareIcon className="size-4" />
          </button>
        )}
      </div>

      {/* Actions menu */}
      <div className="w-8 shrink-0">
        <RowActionsMenu
          actions={actions}
          testIdPrefix="document"
          itemKey={file.name}
          ariaLabel="Document actions"
        />
      </div>
    </div>
  );
};
